/* eslint-disable spire/fenced-imports */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { OffsetOptions, ShiftOptions } from "@floating-ui/dom";
import { UseHoverProps } from "@floating-ui/react/dist/floating-ui.react";
import {
    FloatingArrow,
    FloatingArrowProps,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    Placement,
    useFloatingParentNodeId,
    useTransitionStyles,
} from "@floating-ui/react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import X from "@insite/mobius/Icons/X";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TooltipContext } from "@keller/App/Components/FloatingTooltip/TooltipContext";
import xHeaderUpdate from "@keller/App/Handlers/XNG/Header";
import { FocusableSelectors, useTooltip } from "@keller/App/Hooks/useTooltip";
import Button, { ButtonIcon, ButtonPresentationProps } from "@keller/MobiusOverrides/Button/Button";
import { addScrollbarGutter, getHeaderHeight, mergeWithCss, removeScrollbarGutter } from "@keller/XDL/Utilities";
import React, { FC, FocusEvent, useEffect, useRef, useState } from "react";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { CustomAppState } from "@keller/App/Store/Reducers";

export type FloatingTooltipProps = {
    isOpen?: boolean;
    placement?: Placement;
    trigger?: "hover" | "click" | "manual";
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    offset?: OffsetOptions;
    shift?: ShiftOptions;
    delay?: UseHoverProps["delay"];
    showArrow?: boolean;
    overlay?: boolean;
    triggerComponent: React.ReactNode;
    enabled?: boolean;
    drawer?: boolean;
    extendedStyles?: FloatingTooltipStyles;
};

interface Props extends FloatingTooltipProps {
    children: React.ReactNode;
    isAuthenticated?: boolean;
}

export interface FloatingTooltipStyles {
    tooltipContent?: InjectableCss;
    tooltipArrow?: Omit<FloatingArrowProps, "context">;
    tooltipInnerContent?: InjectableCss;
    drawerContent?: InjectableCss;
    drawerClose?: ButtonPresentationProps;
}

export const floatingTooltipStyles: FloatingTooltipStyles = {};

let currentOpenNodeId: string | null = null;
let closeCurrentOpen: (() => void) | null = null;

const FloatingTooltipComponent: FC<Props> = ({
    children,
    enabled = true,
    triggerComponent,
    overlay = true,
    extendedStyles,
    showArrow = true,
    drawer = false,
    trigger = "hover",
    ...options
}) => {
    const pathname = useAppSelector(o => getLocation(o).pathname);
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const arrowRef = useRef<SVGSVGElement>(null);
    const tooltip = useTooltip({ ...options, showArrow, arrowRef, trigger });
    const { isMounted, styles: translationStyles } = useTransitionStyles(tooltip.context);
    const { isEditing } = useShellContext();
    const headerHeight = getHeaderHeight();

    const [styles] = useState(() => mergeWithCss(floatingTooltipStyles, extendedStyles));
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!isMounted && trigger === "hover") {
            tooltip.setHoverEnabled(true);
        }
    }, [isMounted, trigger, tooltip]);

    // close current tooltip when another is opened
    useEffect(() => {
        if (isMounted) {
            if (currentOpenNodeId && currentOpenNodeId !== tooltip.nodeId) {
                closeCurrentOpen?.();
            }
            currentOpenNodeId = tooltip.nodeId;
            closeCurrentOpen = () => {
                tooltip.setOpen(false);
                options.onOpenChange?.(false);
            };
        } else if (currentOpenNodeId === tooltip.nodeId) {
            currentOpenNodeId = null;
            closeCurrentOpen = null;
        }
    }, [isMounted]);

    // Close tooltip when scrolling outside its bounds
    useEffect(() => {
        if (isTabletScreen || trigger === "click" || typeof window === "undefined") {
            return;
        }

        const handleScroll = (event: Event) => {
            const tooltipElement = tooltip.refs.floating?.current;
            if (tooltipElement && event.target instanceof Node && tooltipElement.contains(event.target)) {
                return;
            }

            tooltip.setOpen(false);
            options.onOpenChange?.(false);
        };

        window.addEventListener("scroll", handleScroll, true);

        return () => {
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [tooltip.refs.floating, tooltip.setOpen, isTabletScreen, trigger]);

    useEffect(() => {
        if (overlay) {
            const menuKey = `${tooltip.nodeId}_tooltip`;
            const isOverlay = isMounted && (trigger !== "manual" || options.isOpen);

            dispatch(
                xHeaderUpdate({
                    key: isOverlay ? "registerMenu" : "unregisterMenu",
                    value: menuKey,
                }),
            );
            isOverlay ? addScrollbarGutter() : removeScrollbarGutter();
            isOverlay ? ariaAppHider.hide(document.body) : ariaAppHider.show(document.body);

            if (drawer) {
                isMounted ? document.body.classList.add("drawer-open") : document.body.classList.remove("drawer-open");
            }
        }
    }, [isMounted, overlay, options.isOpen, drawer]);

    useEffect(() => {
        tooltip.setOpen(false);
        options.onOpenChange?.(false);
        if (overlay) {
            dispatch(
                xHeaderUpdate({
                    key: "unregisterMenu",
                    value: `${tooltip.nodeId}_tooltip`,
                }) as any,
            );
        }
    }, [pathname]);

    useEffect(() => {
        return () => {
            tooltip.setOpen(false);
            options.onOpenChange?.(false);
            if (overlay) {
                removeScrollbarGutter();
                ariaAppHider.show(document.body);
                dispatch(
                    xHeaderUpdate({
                        key: "unregisterMenu",
                        value: drawer ? `${tooltip.nodeId}_drawer` : `${tooltip.nodeId}_tooltip`,
                    }),
                );
            }
        };
    }, []);

    const restoreHover = (floatingElement: HTMLElement | null) => {
        if (trigger !== "hover") {
            return;
        }

        requestAnimationFrame(() => {
            if (document.activeElement && floatingElement?.contains(document.activeElement)) {
                if (
                    document.activeElement.matches(FocusableSelectors) ||
                    (document.activeElement as HTMLElement).isContentEditable
                ) {
                    return;
                }
            }

            tooltip.setHoverEnabled(true);

            const ref = tooltip.refs.reference.current as HTMLElement | null;
            const floatingEl = tooltip.refs.floating.current as HTMLElement | null;
            if (floatingEl && ref && !ref.matches(":hover") && !floatingEl.matches(":hover")) {
                tooltip.setOpen(false);
                options.onOpenChange?.(false);
            }
        });
    };

    if (!enabled || isEditing) {
        return <>{triggerComponent}</>;
    }
    return (
        <FloatingNode id={tooltip.nodeId}>
            <TooltipContext.Provider value={{ ...tooltip, open: isMounted }}>
                <span
                    ref={tooltip.refs.setReference}
                    {...tooltip.getReferenceProps()}
                    data-state={isMounted ? "open" : "closed"}
                >
                    {triggerComponent}
                </span>
                <FloatingPortal>
                    <StyledWrapper
                        ref={tooltip.refs.setFloating}
                        style={
                            !drawer
                                ? {
                                      ...tooltip.floatingStyles,
                                      ...translationStyles,
                                      display: isMounted ? "block" : "none",
                                  }
                                : {}
                        }
                        {...{
                            isAuthenticated: options.isAuthenticated,
                            headerHeight,
                            // handle mobile offset
                            offset: (headerHeight ?? 0) + (typeof window !== "undefined" ? window.scrollY : 0),
                        }}
                        {...(drawer ? styles.drawerContent : styles.tooltipContent)}
                        {...{ isMounted }}
                        {...tooltip.getFloatingProps({
                            onFocus(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    tooltip.setHoverEnabled(false);
                                }
                            },
                            onBlur(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    restoreHover(event.currentTarget as HTMLElement);
                                }
                            },
                            onPointerLeave() {
                                if (trigger === "hover") {
                                    restoreHover(tooltip.refs.floating.current as HTMLElement | null);
                                }
                            },
                        })}
                    >
                        {drawer && (
                            <Button
                                {...styles.drawerClose}
                                onClick={() => tooltip.setOpen(false)}
                                aria-label={translate("close")}
                            >
                                <ButtonIcon src={X} size={15} />
                            </Button>
                        )}
                        <StyledWrapper {...styles.tooltipInnerContent} {...{ headerHeight }}>
                            {children}
                        </StyledWrapper>
                        {!drawer && showArrow && (
                            <FloatingArrow ref={arrowRef} context={tooltip.context} {...styles.tooltipArrow} />
                        )}
                    </StyledWrapper>
                </FloatingPortal>
            </TooltipContext.Provider>
        </FloatingNode>
    );
};

export function FloatingTooltip(props: Props) {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
        return (
            <FloatingTree>
                <FloatingTooltipComponent {...props} />
            </FloatingTree>
        );
    }
    return <FloatingTooltipComponent {...props} />;
}

export default FloatingTooltip;
